import { CalendarDays, CalendarRange, ClipboardList, ShoppingCart, Users } from 'lucide-react';

import { defineArea } from '../../app/area';
import { RequirePermission } from '../../auth/guards';
import { DeliveryCalendarPage } from './pages/DeliveryCalendarPage';
import { DeliverySchedulePage } from './pages/DeliverySchedulePage';
import { SalesOrderDetailPage } from './pages/SalesOrderDetailPage';
import { SalesOrdersPage } from './pages/SalesOrdersPage';
import { StaffOrderDetailPage } from './pages/StaffOrderDetailPage';
import { StaffOrdersPage } from './pages/StaffOrdersPage';
import { PortalOrderPage } from './portal/PortalOrderPage';
import { PortalOrderDetailPage, PortalOrdersPage } from './portal/PortalOrdersPage';

/**
 * OWNER: the `sales-orders` module agent (M4 — docs/MODULES.md).
 * Owns: apps/web/src/areas/sales-orders/**  ·  apps/api/src/modules/sales-orders/**
 *       packages/shared/src/contracts/sales-orders.ts
 *       packages/shared/src/i18n/{sv,en,zh}/salesOrders.json  ·  e2e/sales-orders.md
 *
 * This area owns BOTH chromes: the admin order lists AND the restaurant
 * portal's ordering flow (`/portal` index + `/portal/orders`). The portal index
 * is the ordering screen itself — a restaurant user lands there after login
 * (`homePathFor`, auth-store.ts).
 *
 * `RequirePermission` wraps the fully-gated subtrees so a user without the key
 * gets the "Ingen behörighet" screen instead of a page full of 403s. It is
 * chrome only — every endpoint re-checks server-side.
 */
export default defineArea({
  id: 'sales-orders',
  nav: [
    {
      section: 'orders',
      to: '/orders/sales',
      labelKey: 'common:nav.orders.salesOrders',
      icon: ShoppingCart,
      permissions: ['orders.outbound.list'],
      order: 10,
    },
    {
      section: 'orders',
      to: '/orders/staff',
      labelKey: 'common:nav.orders.staffOrders',
      icon: Users,
      permissions: ['orders.staff.create', 'orders.staff.manage'],
      order: 60,
    },
    {
      section: 'orders',
      to: '/orders/delivery-schedule',
      labelKey: 'common:nav.orders.deliverySchedule',
      icon: CalendarRange,
      permissions: ['orders.outbound.deliverySchedule'],
      order: 70,
    },
    {
      section: 'orders',
      to: '/orders/calendar',
      labelKey: 'common:nav.orders.calendar',
      icon: CalendarDays,
      permissions: ['orders.outbound.list'],
      order: 80,
    },
  ],
  routes: [
    {
      path: 'orders/sales',
      element: (
        <RequirePermission permissions={['orders.outbound.list']}>
          <SalesOrdersPage />
        </RequirePermission>
      ),
    },
    {
      path: 'orders/sales/:id',
      element: (
        <RequirePermission permissions={['orders.outbound.list']}>
          <SalesOrderDetailPage />
        </RequirePermission>
      ),
    },
    {
      path: 'orders/staff',
      element: (
        <RequirePermission permissions={['orders.staff.create', 'orders.staff.manage', 'orders.outbound.list']}>
          <StaffOrdersPage />
        </RequirePermission>
      ),
    },
    {
      path: 'orders/staff/:id',
      element: (
        <RequirePermission permissions={['orders.staff.create', 'orders.staff.manage', 'orders.outbound.list']}>
          <StaffOrderDetailPage />
        </RequirePermission>
      ),
    },
    {
      path: 'orders/delivery-schedule',
      element: (
        <RequirePermission permissions={['orders.outbound.deliverySchedule']}>
          <DeliverySchedulePage />
        </RequirePermission>
      ),
    },
    {
      path: 'orders/calendar',
      element: (
        <RequirePermission permissions={['orders.outbound.list']}>
          <DeliveryCalendarPage />
        </RequirePermission>
      ),
    },
  ],
  portalNav: [
    {
      to: '/portal',
      labelKey: 'shell:portal.order',
      icon: ShoppingCart,
      permissions: ['orders.portal.order'],
      order: 10,
      end: true,
    },
    {
      to: '/portal/orders',
      labelKey: 'shell:portal.myOrders',
      icon: ClipboardList,
      permissions: ['orders.outbound.list'],
      order: 20,
    },
  ],
  portalRoutes: [
    { index: true, element: <PortalOrderPage /> },
    { path: 'orders', element: <PortalOrdersPage /> },
    { path: 'orders/:id', element: <PortalOrderDetailPage /> },
  ],
});
